import { type ComponentType, type FC, useMemo } from 'react';

import { useCms } from './provider';
import { RenderingProvider } from './rendering-provider';

interface RawRenderingProps {
  componentName?: string;
  uid?: string;
  fields?: unknown;
  params?: unknown;
  placeholders?: unknown;
}

/**
 * Mounts a component that reads its rendering with `useContent` where it receives the raw rendering
 * as props instead: the Sitecore lane's registry, whose placeholder spreads `fields`/`params` onto
 * the component and fills only its own context, not this package's. The active adapter converts
 * those props, so the component reads what `<Placeholder>` would have handed it.
 */
export const withContentFromProps = (Component: ComponentType, componentName: string): FC<RawRenderingProps> => {
  const WithContentFromProps: FC<RawRenderingProps> = ({
    componentName: name = componentName,
    uid,
    fields,
    params,
    placeholders,
  }) => {
    const { adapter } = useCms();
    const rendering = useMemo(
      () => adapter.toRendering({ componentName: name, uid, fields, params, placeholders }),
      [adapter, name, uid, fields, params, placeholders],
    );

    return (
      <RenderingProvider rendering={rendering}>
        <Component />
      </RenderingProvider>
    );
  };
  WithContentFromProps.displayName = `withContentFromProps(${componentName})`;
  return WithContentFromProps;
};
